import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssessmentResponseSummary } from './assessments';
import { api } from './api';

export interface SnapshotListEntry {
  id: string;
  generatedAt: string;
  assessmentResponseId: number | null;
  // Null only on legacy rows generated before the column existed.
  compliancePct: number | null;
  // Optional human label set at generation time.
  name: string | null;
  // Optional free-form notes set at generation time.
  notes: string | null;
  // The catalog release the report was scored against, and when. Null on
  // snapshots generated before releases existed.
  catalogReleaseVersion: number | null;
  scoredAsOf: string | null;
  generatedBy: { id: number; name: string | null; email: string };
}

export interface GenerateSnapshotInput {
  name?: string;
  notes?: string;
}

export interface SnapshotDetail extends SnapshotListEntry {
  // Backend returns the captured summary under `payload` (Prisma column name),
  // not `data`. Snapshots service: snapshots.service.ts:73-83.
  payload: AssessmentResponseSummary;
}

export function useSnapshots(companyId: number | undefined) {
  return useQuery({
    queryKey: ['snapshots', companyId],
    queryFn: () => api.get<SnapshotListEntry[]>('/api/Snapshots'),
    enabled: !!companyId,
  });
}

export function useSnapshot(companyId: number | undefined, snapshotId: string | undefined) {
  return useQuery({
    queryKey: ['snapshot', companyId, snapshotId],
    queryFn: () => api.get<SnapshotDetail>(`/api/Snapshots/${snapshotId}`),
    enabled: !!companyId && !!snapshotId,
  });
}

export function useGenerateSnapshot(companyId: number | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateSnapshotInput = {}) =>
      api.post<SnapshotListEntry>('/api/Snapshots', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['snapshots', companyId] });
      qc.invalidateQueries({ queryKey: ['response-summary', companyId] });
    },
  });
}
